import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from '../../../../shared/i18n';

// Default interval for progress updates
const DEFAULT_UPDATE_INTERVAL_MS = 100;

// Minimum seconds to display (below this, show "Almost done...")
const MIN_DISPLAY_SECONDS = 3;

/**
 * TimeBasedProgressBar - Shows a time-based progress indicator
 *
 * The progress bar moves based on elapsed time relative to the average expected time.
 * Progress is capped at 95% to indicate ongoing activity until completion.
 * Shows estimated remaining time, but stops showing specific seconds below 3s
 * to avoid stressing users with precise countdowns.
 *
 * Props:
 * @param {number} averageSeconds - Expected average time in seconds (default: 10)
 * @param {number} startTime - Optional start timestamp (Date.now()), defaults to component mount time
 * @param {number} updateIntervalMs - Update interval in milliseconds (default: 100)
 * @param {function} getCurrentTime - Optional function to get current time (useful for testing)
 */
export const TimeBasedProgressBar = ({
  averageSeconds = 10,
  startTime = null,
  updateIntervalMs = DEFAULT_UPDATE_INTERVAL_MS,
  getCurrentTime = () => Date.now()
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(averageSeconds);
  const startTimeRef = useRef(startTime || getCurrentTime());

  useEffect(() => {
    // Update start time ref if prop changes
    if (startTime !== null) {
      startTimeRef.current = startTime;
    }
  }, [startTime]);

  useEffect(() => {
    // Progress bar animation - updates every updateIntervalMs
    const interval = setInterval(() => {
      const elapsedMs = getCurrentTime() - startTimeRef.current;
      const elapsedSec = elapsedMs / 1000;

      // Calculate remaining time (minimum 0)
      const remaining = Math.max(0, averageSeconds - elapsedSec);
      setRemainingSeconds(remaining);

      // Calculate progress based on elapsed time vs average expected time
      // Progress = (elapsed / average) * 100, capped at 95%
      const calculatedProgress = (elapsedSec / averageSeconds) * 100;
      setProgress(Math.min(calculatedProgress, 95));
    }, updateIntervalMs);

    return () => clearInterval(interval);
  }, [averageSeconds, updateIntervalMs, getCurrentTime]);

  // Display remaining seconds, but stop at MIN_DISPLAY_SECONDS to show "Almost done..."
  const displaySeconds = Math.ceil(remainingSeconds);

  return (
    <div className="w-100" style={{ maxWidth: '300px' }}>
      <div className="progress mb-2" style={{ height: '10px' }}>
        <div
          className="progress-bar bg-primary"
          role="progressbar"
          style={{
            width: `${progress}%`,
            transition: 'width 0.1s linear'
          }}
          aria-valuenow={Math.round(progress)}
          aria-valuemin="0"
          aria-valuemax="100"
          data-testid="progress-bar"
        />
      </div>
      <p className="text-secondary small mb-0 text-center" data-testid="remaining-time">
        {displaySeconds > MIN_DISPLAY_SECONDS
          ? t('children.activity_sessions.progress_bar.seconds_remaining', { seconds: displaySeconds })
          : t('children.activity_sessions.progress_bar.almost_done')}
      </p>
    </div>
  );
};

export default TimeBasedProgressBar;
